import React, { useEffect, useRef } from "react";
import MatrixRain from "../components/MatrixRain";
import { Footer } from "../components/Sections";
import { ASSETS } from "../mock";
import { usePageTracking, track } from "../analytics";
import { whopTrack, fireGoogleAdsConversion, PIXEL_CONFIG } from "../pixels";

const VIDALYTICS_ACCOUNT = "05PByEt0";
const THANKYOU_VIDEO_ID = "Sgz1PAYCDPJurIRF";
const PREP_VIDEOS = [
  { id: "lXAL9I4v_fzK5wJg", title: "How Does TMU Trade?" },
  { id: "RwboiRSdtwJB9OMZ", title: "Is TMU Legit?" },
  { id: "0DwiPGM7B7crO6H4", title: "Do I Need To Have Prior Experience?" },
  { id: "BzNCUcnJLk_I8qlt", title: "How Is TMU Structured?" },
];

const VidalyticsVideo = ({ id }) => {
  const embedId = `vidalytics_embed_${id}`;
  useEffect(() => {
    const set = window.__vidInit || (window.__vidInit = new Set());
    if (set.has(id)) return;
    set.add(id);
    const base = `https://fast.vidalytics.com/embeds/${VIDALYTICS_ACCOUNT}/${id}/`;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.text = `(function (v, i, d, a, l, y, t, c, s) {
      y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
      if (!vsl){vsl=function(u,cb){
        if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
        if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
        i.getElementsByTagName("head")[0].appendChild(s);
      };}
      vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
    })(window, document, 'Vidalytics', '${embedId}', '${base}');`;
    document.body.appendChild(script);
  }, [id, embedId]);

  return (
    <div
      id={embedId}
      style={{ width: "100%", position: "relative", paddingTop: "56.25%" }}
    />
  );
};

const ThankYouPage = () => {
  usePageTracking("thankyou");
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    track("thankyou_page_view", { funnel: "vsl", step: 5 });
    whopTrack("thankyou_page_view", { funnel: "vsl", step: 5 });
    // Fire "call_booked" ONLY when the booking came from /calendar-booking
    // (flag set there) and is now confirmed by landing on /thankyou.
    if (sessionStorage.getItem("tmu_vsl_booked") === "1") {
      sessionStorage.removeItem("tmu_vsl_booked");
      const txn = sessionStorage.getItem("tmu_vsl_txn") || `vsl_${Date.now()}`;
      sessionStorage.removeItem("tmu_vsl_txn");
      const eventUri = sessionStorage.getItem("tmu_vsl_event_uri") || "";
      sessionStorage.removeItem("tmu_vsl_event_uri");
      track("call_booked", { funnel: "vsl", source: "calendar-booking", confirmed: true });
      // event_id matches the server-side call_booked (call_{event_uri}) fired by
      // POST /vsl/booking, so Whop de-duplicates them into one conversion.
      whopTrack("call_booked", {
        funnel: "vsl",
        source: "calendar-booking",
        event_id: eventUri ? `call_${eventUri}` : undefined,
      });
      fireGoogleAdsConversion(PIXEL_CONFIG.GADS_CALL_BOOKED, {
        eventKey: "call_booked_vsl",
        transactionId: `call_${txn}`,
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <header className="relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[360px]">
          <MatrixRain />
          <div className="matrix-fade" />
        </div>

        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-4 pt-10 sm:px-5">
          <img
            src={ASSETS.logoEmblem}
            alt="The Matrix Unlocked"
            className="h-16 w-auto sm:h-20"
          />
          <h1 className="mt-5 text-balance text-center text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl md:text-[52px]">
            Your Meeting Has Been <span className="text-[#16E36F]">Confirmed</span>
          </h1>
          <p className="mt-4 max-w-2xl text-balance text-center text-lg font-medium leading-snug text-neutral-300 sm:text-xl">
            Check your email for the confirmation email.
          </p>
        </div>

        <div className="relative z-10 mx-auto mt-8 w-full max-w-4xl px-4 pb-10 sm:px-5">
          <div
            className="overflow-hidden rounded-2xl border border-[#16E36F]/25 bg-[#050806] shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
            data-testid="thankyou-video-wrap"
          >
            <VidalyticsVideo id={THANKYOU_VIDEO_ID} />
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-balance text-center text-lg font-medium leading-snug text-neutral-300 sm:text-xl">
            Watch the following important videos to prepare for the call.
          </p>
        </div>

        {/* Split sections: 2 videos per row on desktop, stacked on mobile */}
        <div className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-20 sm:px-5">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
            {PREP_VIDEOS.map((v) => (
              <div key={v.id} data-testid={`prep-video-${v.id}`}>
                <h3 className="mb-3 text-center text-lg font-semibold text-white sm:text-xl">
                  {v.title}
                </h3>
                <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#050806] shadow-[0_16px_60px_rgba(0,0,0,0.5)]">
                  <VidalyticsVideo id={v.id} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </header>

      <Footer />
    </div>
  );
};

export default ThankYouPage;
